import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ProgressBar } from '../atomic/progress';
import { DialogBox, DialogBoxFooter } from '../atomic/dialog';
import {
    createDirectory,
    directoryPicker,
    downloadFile,
} from '../../utils/filesystem';
import { FilesAPI } from '../../APIs/filesApi';
import { getAsync, useGet } from '../../hooks/useApi';

interface FileInfo {
    url: string;
    name: string;
}

export const FolderDownloadDialog = ({ setOpen, folderId }) => {
    const processingRef = React.useRef(false);
    const [fileProcessing, setFileProcessing] = useState<boolean>(true);
    const [files, setFiles] = useState<Array<FileInfo>>([]);
    const [total, setTotal] = useState<number>(0);

    const [percent, setPercent] = useState<number>(0);
    const [message, setMessage] = useState<string>('読み込み中');

    const [cancelText, setCancelText] = useState<string>('キャンセル');

    const getFile = async ({ parentFolderId, offset }) => {
        const items = await getAsync(
            FilesAPI._items({
                parentFolderId,
                file: true,
                frame: true,
                folder: false,
                offset,
            })
        );
        if (items.data.length) {
            const files = items.data.map((file) => {
                return {
                    name: file.name,
                    url: file.url,
                } as FileInfo;
            });
            setFiles((prev) => {
                const _files = [...prev, ...files];
                setTotal(_files.length);
                return _files;
            });
            if (items.pagination?.hasNextPage) {
                setMessage(
                    `読み込み中(${items.pagination.page}/${items.pagination.pageLength})`
                );
                getFile({
                    parentFolderId: folderId,
                    offset: items.pagination.nextOffset,
                });
            } else {
                setFileProcessing(false);
            }
        } else {
            setMessage(`DL可能なファイルがありません`);
            setCancelText('閉じる');
        }
    };
    useEffect(() => {
        getFile({ parentFolderId: folderId, offset: 0 });
    }, []);

    useEffect(() => {
        if (!fileProcessing) {
            setMessage(`${files.length}枚のファイルをダウンロード`);
        }
    }, [fileProcessing]);
    useEffect(() => {
        setPercent(1 - files.length / total);
    }, [files.length, total]);

    const cancelDownload = (msg) => {
        setPercent(0);
        processingRef.current = false;
        setMessage(msg);
        setCancelText('閉じる');
    };

    const fileDownLoad = async (filesDirHandle) => {
        if (!processingRef.current) {
            cancelDownload('キャンセルしました');
            return;
        }
        const file = files.shift();
        if (file) {
            try {
                const name = await downloadFile(
                    filesDirHandle,
                    file.url,
                    file.name
                );
                setMessage(
                    `${name}をダウンロード中(${total - files.length}/${total})`
                );
            } catch (e) {
                cancelDownload('エラーが発生しました');
                return;
            }
        }
        if (files.length) {
            setFiles([...files]);
            fileDownLoad(filesDirHandle);
        } else {
            setPercent(1);
            processingRef.current = false;
            setMessage('ダウンロード完了');
            setCancelText('閉じる');
        }
    };

    const onOK = useCallback(async () => {
        const dirHandle = await directoryPicker();
        processingRef.current = true;
        setFileProcessing(true);
        fileDownLoad(dirHandle);
    }, [files]);

    const onCancel = useCallback(() => {
        if (processingRef.current) {
            setMessage('ダウンロードを中止しました');
            processingRef.current = false;
        } else {
            setOpen(false);
        }
    }, [processingRef.current]);

    return (
        <DialogBox
            title="ダウンロード"
            onOK={onOK}
            okText="ダウンロード"
            disabled={processingRef.current || fileProcessing}
            onCancel={onCancel}
            cancelText={cancelText}
        >
            {message}
            <DialogBoxFooter>
                {processingRef.current && <ProgressBar percent={percent} />}
            </DialogBoxFooter>
        </DialogBox>
    );
};
